<template>
  <ColumnAdjusterCard
  elementId="settlements-column-adjuster"
  :propColumnSets="columnSets"
  :dialogColor="contractColor"
  :mobileMode="mobileMode"
  showClose
  @apply="$emit('apply')"
  @close="$emit('close')">
    <template #default="{ columnSets: editedColumnSets }">
      <v-tabs v-model="currentTab" show-arrows>
        <v-tab v-for="tab in tabs" :key="tab.key">
          {{$t(tab.text)}}
        </v-tab>

        <v-tab-item v-for="tab in tabsWithColumnSets(editedColumnSets)" :key="tab.key">
          <v-row class="mt-1">
            <v-col cols="12" v-for="section in tab.sections" :key="section.key">
              <v-card outlined>
                <v-card-title class="subtitle-1 font-weight-medium">
                  {{$t(section.text)}}
                  <v-spacer/>
                  <Icon
                  :icon="`mdi-checkbox-multiple-${allVisible(section.columnSet) ? 'marked' : 'blank-outline'}`"
                  :iconColor="allVisible(section.columnSet) ? 'success' : 'rgba(0,0,0,0.3)'"
                  :tooltipText="$t(allVisible(section.columnSet) ? 'tabSelectNoneCheckboxTooltip' : 'tabSelectAllCheckboxTooltip', { tabType: $t(section.text) })"
                  tooltipColor="primary"
                  :small="false"
                  @icon-clicked="section.columnSet.toggleColumns(section.columnSet.getAdjustableColumns())"/>
                </v-card-title>
                <v-card-text>
                  <ColumnToggleGrid
                  :columns="section.columnSet.getAdjustableColumns()"
                  @toggle="column => section.columnSet.toggleColumn(column.id)"/>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-tab-item>
      </v-tabs>
    </template>
  </ColumnAdjusterCard>
</template>

<script>
import { settlementsByContractHeaders, settlementsByPayeeHeaders, journalEntrySettlementByPayeeHeaders, bpReceivablesByPayerHeaders, settlementProductHeaders, contractTicketHeaders, tractSummaryHeaders, ticketRegisterContractSummaryHeaders } from '@/headers/Cycles.js'
import { settlementAdvanceHeaders, settlementTractPaymentHeaders, settlementAccountPaymentHeaders } from '@/headers/ExportBatch.js'
import { SettlementKeys, SettlementSubKeys, SettlementColumnContexts } from '@/components/settlements/SettlementKeys.js'
import { ContractMode } from '@/utils/Enumerations.js'
import { mapGetters } from 'vuex'

export default {
  name: 'SettlementsColumnAdjusterCard',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    ColumnAdjusterCard: () => import('@/components/core/ColumnAdjusterCard.vue'),
    ColumnToggleGrid: () => import('@/components/core/ColumnToggleGrid.vue')
  },

  props: {
    isByProducts: {
      type: Boolean,
      default: false
    },
    mobileMode: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    tabs: [],
    columnSets: [],
    currentTab: 0
  }),

  computed: {
    ...mapGetters('settlements', ['currentModuleKey', 'currentSubModuleKey']),

    contractColor () {
      return (this.isByProducts ? ContractMode.Byproducts : ContractMode.Logs).details.color
    }
  },

  created () {
    this.tabs = this.tabsForModule()
    this.columnSets = this.tabs.flatMap(tab => tab.sections.map(section => section.columnSet))
    this.currentTab = Math.max(0, this.tabs.findIndex(t => t.key === this.currentSubModuleKey))
  },

  methods: {
    tabsForModule () {
      switch (this.currentModuleKey) {
        case SettlementKeys.AccountsPayable: return this.isByProducts ? this.byproductPayableTabs() : this.accountsPayableTabs()
        case SettlementKeys.AccountsReceivable: return this.byproductReceivableTabs()
        case SettlementKeys.Accruals: return this.accrualsTabs()
        default: return []
      }
    },

    byproductPayableTabs () {
      const byPayee = SettlementColumnContexts.ByproductSettlementsByPayee
      return [
        {
          key: SettlementSubKeys.AP.ByproductSettlementsByPayee,
          text: 'byPayee',
          sections: [
            { key: 'summary', text: 'summary', columnSet: settlementsByPayeeHeaders(false, byPayee) },
            { key: 'activitySummary', text: 'activitySummary', columnSet: settlementProductHeaders(byPayee) },
            { key: 'ticketSummary', text: 'ticketSummary', columnSet: contractTicketHeaders(byPayee) }
          ]
        }
      ]
    },

    byproductReceivableTabs () {
      const byPayer = SettlementColumnContexts.ByproductReceivablesByPayer
      return [
        {
          key: SettlementSubKeys.AR.BPReceivablesByPayer,
          text: 'receivableByPayer',
          sections: [
            { key: 'summary', text: 'summary', columnSet: bpReceivablesByPayerHeaders(byPayer) },
            { key: 'activitySummary', text: 'activitySummary', columnSet: settlementProductHeaders(byPayer) },
            { key: 'ticketSummary', text: 'ticketSummary', columnSet: contractTicketHeaders(byPayer) }
          ]
        }
      ]
    },

    accountsPayableTabs () {
      const byContract = SettlementColumnContexts.SettlementsByContract
      const byPayee = SettlementColumnContexts.SettlementsByPayee
      const ticketRegister = SettlementSubKeys.AP.TicketRegister
      const miscPayments = SettlementSubKeys.AP.MiscPayments
      return [
        {
          key: SettlementSubKeys.AP.SettlementsByContract,
          text: 'byContract',
          sections: [
            { key: 'summary', text: 'summary', columnSet: settlementsByContractHeaders() },
            { key: 'activitySummary', text: 'activitySummary', columnSet: settlementProductHeaders(byContract) },
            { key: 'ticketSummary', text: 'ticketSummary', columnSet: contractTicketHeaders(byContract) }
          ]
        },
        {
          key: SettlementSubKeys.AP.SettlementsByPayee,
          text: 'byPayee',
          sections: [
            { key: 'summary', text: 'summary', columnSet: settlementsByPayeeHeaders(true, byPayee) },
            { key: 'activitySummary', text: 'activitySummary', columnSet: settlementProductHeaders(byPayee) },
            { key: 'ticketSummary', text: 'ticketSummary', columnSet: contractTicketHeaders(byPayee) }
          ]
        },
        {
          key: ticketRegister,
          text: 'ticketRegister',
          sections: [
            { key: 'tractSummaries', text: 'tractSummaries', columnSet: tractSummaryHeaders() },
            { key: 'contractSummaries', text: 'contractSummaries', columnSet: ticketRegisterContractSummaryHeaders() }
          ]
        },
        {
          key: miscPayments,
          text: 'miscPayments',
          sections: [
            { key: 'advances', text: 'advances', columnSet: settlementAdvanceHeaders(false, miscPayments) },
            { key: 'tractPayments', text: 'tractPayments', columnSet: settlementTractPaymentHeaders(miscPayments) },
            { key: 'accountPayments', text: 'accountPayments', columnSet: settlementAccountPaymentHeaders(miscPayments) }
          ]
        }
      ]
    },

    accrualsTabs () {
      const byPayee = SettlementColumnContexts.AccrualsByPayee
      return [
        {
          key: SettlementSubKeys.AC.SettlementsByPayee,
          text: 'byPayee',
          sections: [
            { key: 'summary', text: 'summary', columnSet: journalEntrySettlementByPayeeHeaders(true, byPayee) },
            { key: 'activitySummary', text: 'activitySummary', columnSet: settlementProductHeaders(byPayee) },
            { key: 'ticketSummary', text: 'ticketSummary', columnSet: contractTicketHeaders(byPayee) }
          ]
        }
      ]
    },

    tabsWithColumnSets (editedColumnSets) {
      return this.tabs.map(tab => ({
        ...tab,
        sections: tab.sections.map(section => ({ ...section, columnSet: editedColumnSets[this.columnSets.indexOf(section.columnSet)] }))
      }))
    },

    allVisible (columnSet) {
      return columnSet.getAdjustableColumns().every(c => c.visible)
    }
  }
}
</script>
